import { Component, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { ActivatedRoute, ParamMap, Router } from '@angular/router';

import lodash from 'lodash';
import moment from 'moment';
import { Subscription } from 'rxjs';
import { debounceTime } from 'rxjs/operators';

import { TimeService } from '@bayclubs/utils';

import { CategoryModel, ContextModel, GetGroupClassesQuery, GroupClassesModel, GroupClassModel } from '@bayclubs/group-classes-employee-api';
import { GlobalEventsService, OnAddEditGroupClass } from '../../../infrastructure/infrastructure.module';
import { GroupClassesApiService } from '../../../shared/shared.module';
import { SecuritySignalService } from '@infrastructure/services/security-signal.service';
import { bindPageHeaderAction } from '@shared/components/page-header/page-header.actions';

@Component({
    selector: 'app-group-classes-list',
    templateUrl: './group-classes-list.component.html',
    styleUrls: ['./group-classes-list.component.scss'],
    standalone: false
})
export class GroupClassesListComponent implements OnInit, OnDestroy {
    public model: GroupClassesModel;
    public form: UntypedFormGroup;
    private pageNumber = 0;
    private onGroupClassAdded: Subscription;
    private onGroupClassUpdated: Subscription;
    private initialQueryParams: ParamMap;
    public context: ContextModel;

    private readonly addClass = () => {
        this.globalEvents.onAddEditGroupClass$.emit(new OnAddEditGroupClass());
    }

    constructor(
        private api: GroupClassesApiService,
        private formBuilder: UntypedFormBuilder,
        private time: TimeService,
        private router: Router,
        private globalEvents: GlobalEventsService,
        activatedRoute: ActivatedRoute,
        private readonly securitySignals: SecuritySignalService
    ) {
        this.initialQueryParams = activatedRoute.snapshot.queryParamMap;
        this.context = lodash.cloneDeep(activatedRoute.snapshot.data.context);

        bindPageHeaderAction(
            () => this.securitySignals.canManageClasses(),
            'Add class',
            this.addClass
        );
    }

    ngOnInit() {
        this.initForm();
        this.onSearch(0);
        this.onGroupClassAdded = this.globalEvents.onGroupClassAdded$.subscribe(() => this.onSearch(0));
        this.onGroupClassUpdated = this.globalEvents.onGroupClassUpdated$.subscribe(() => this.onSearch(0));
    }

    ngOnDestroy() {
        this.onGroupClassAdded.unsubscribe();
        this.onGroupClassUpdated.unsubscribe();
    }

    public loadNextPage(): void {
        this.onSearch(this.pageNumber + 1);
    }

    public viewDetails(item: GroupClassModel) {
        this.router.navigate(['management/classes/' + item.id]);
    }

    private onSearch(pageNumber: number): void {
        const criteria = {
            pageNumber: pageNumber,
            pageSize: 20,
            name: this.form.value.name,
            instructor: this.form.value.instructor,
            clubs: this.form.value.clubs,
            statuses: this.form.value.statuses,
            categoryCodes: this.form.value.categories?.map(c => c.code),
            dateFrom: this.form.value.dateFrom,
            dateTo: this.form.value.dateTo,
            classesThatRequireBooking: this.form.value.onlyBookingRequired,
            onlineClasses: this.form.value.online,
            isVisibleToConnect: this.form.value.isVisibleToConnect,
            showOnlySubstitutedClasses: this.form.value.substitutedOnly
        } as GetGroupClassesQuery;

        this.api.get(criteria).subscribe(result => {
            if (pageNumber === 0) {
                this.model = result;
            } else {
                this.model.hasMoreItems = result.hasMoreItems;
                result.items.forEach(i => this.model.items.push(i));
            }

            this.pageNumber = pageNumber;
        });
    }

    private initForm() {
        const queryParamsDateFrom = this.initialQueryParams.get('dateFrom');
        const queryParamsDateTo = this.initialQueryParams.get('dateTo');

        const initialDateFrom = queryParamsDateFrom ?
        this.time.toBcDate(moment(queryParamsDateFrom)) : this.time.toBcDate(this.time.todayInDefaultTimeZone());

        const categoriesCodes: string[] = this.initialQueryParams.getAll('categories');
        const categories: CategoryModel[] = this.context.categories.filter(c =>
            lodash.includes(categoriesCodes, c.code)
        );
        this.form = this.formBuilder.group({
            name: [null, []],
            instructor: [this.initialQueryParams.get('instructor'), []],
            dateFrom: [initialDateFrom, []],
            dateTo: [queryParamsDateTo ? this.time.toBcDate(moment(queryParamsDateTo)) : null, []],
            clubs: [this.initialQueryParams.getAll('clubs'), []],
            categories: [categories, []],
            statuses: [this.initialQueryParams.getAll('statuses'), []],
            onlyBookingRequired: [this.initialQueryParams.get('onlyBookingRequired'), []],
            online: [this.initialQueryParams.get('online'), []],
            isVisibleToConnect: [this.initialQueryParams.get('isVisibleToConnect'), []],
            substitutedOnly: [this.initialQueryParams.get('substitutedOnly'), []],
        });

        this.form.valueChanges.pipe(debounceTime(500)).subscribe(() => {
            this.onSearch(0);
        });
    }
}
